import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { BottomButtonsLayout, theme } from 'aimo-ui';
import CrickleCutCardBackground from 'components/common/CrickleCutCardBackground';
import AccountContext from 'context/AccountContext';
import useMultiPoolPermitData from 'hooks/useMultiPoolPermitData';
import useUpdatePermit from 'hooks/useUpdatePermit';
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView } from 'react-native';
import { usePermitStore } from 'stores/permitStore';
import { ColumnWithMargins } from 'styles/ContainerStyles';
import {
  AccessPermitPoolingGroupCombinedInfo,
  Maybe,
} from 'types/generatedSchemaTypes';
import { RootNavProps, RootStackParamList } from 'types/navigation';
import ModalContentWrapper from '../ModalContentWrapper';
import PermitTicketHeader from '../PermitTicketHeader';
import { PermitContentWrapper } from '../styles';
import PermitBottomButtons from './PermitBottomButtons';
import { PoolingGroupRow } from './PoolingGroupRow';

const MultiPoolPermit: React.FC = () => {
  const { t } = useTranslation();
  const { navigate } = useNavigation<RootNavProps>();
  const {
    params: { permitId },
  } = useRoute<RouteProp<RootStackParamList, 'multiPoolingPermit'>>();

  const {
    customer: { permits },
  } = useContext(AccountContext);

  const { updatePermit, loading } = useUpdatePermit(permitId, {});
  const {
    availableSpots,
    poolingGroups,
    showLicensePlate,
    usageType,
    isWaitingActivation,
    licensePlatesInPermit,
  } = useMultiPoolPermitData(permitId, permits ?? []);

  const selectedPlates = usePermitStore(
    (state) => state.selectedLicensePlatesPermitRedeem
  );

  const hasPlateChanges = useMemo(() => {
    return (
      JSON.stringify(selectedPlates || []) !==
      JSON.stringify(licensePlatesInPermit || [])
    );
  }, [selectedPlates, licensePlatesInPermit]);

  const handleSave = useCallback(() => {
    const sanitizedPlates = (selectedPlates || []).map(
      ({ plateText, countryCode }) => ({ plateText, countryCode })
    );

    updatePermit({
      variables: {
        input: {
          permitId,
          registrationPlates: sanitizedPlates,
          permitActionType: 'PATCH',
        },
      },
    });
  }, [permitId, updatePermit, selectedPlates]);

  useEffect(() => {
    if (permitId) {
      usePermitStore
        .getState()
        .setSelectedLicensePlatesPermitRedeem(licensePlatesInPermit || []);
    }

    return () => {
      usePermitStore.getState().setSelectedLicensePlatesPermitRedeem([]);
    };
  }, [permitId, licensePlatesInPermit]);

  const renderPoolingGroupRow = (
    group: Maybe<AccessPermitPoolingGroupCombinedInfo>,
    index: number
  ) => {
    if (!group) {
      return null;
    }

    return (
      <PoolingGroupRow
        key={`pooling-group-${group.uid}`}
        group={group}
        index={index}
        t={t}
        onPress={() => {
          navigate('poolingGroup', {
            permitId,
            poolingGroupUid: group.uid ?? '',
          });
        }}
      />
    );
  };

  const renderBottomButtons = () => {
    if (!showLicensePlate) {
      return null;
    }

    return (
      <ColumnWithMargins mr={20} ml={20} mb={theme.normalising.heightPixel(30)}>
        <PermitBottomButtons
          onPress={handleSave}
          isRedeemed={!isWaitingActivation || !showLicensePlate}
          isRedeemable={showLicensePlate}
          loading={loading}
          disabled={!hasPlateChanges}
          usageType={usageType}
          showLicensePlate={showLicensePlate}
        />
      </ColumnWithMargins>
    );
  };

  return (
    <BottomButtonsLayout
      bottomButtonsBackgroundColor={theme.colors.white}
      contentBottom={renderBottomButtons()}>
      <ScrollView>
        <ModalContentWrapper>
          <CrickleCutCardBackground
            cutColor={theme.colors.lightBg.lilac}
            aboveCutView={
              <PermitTicketHeader
                title={t('permit.access.poolingPermit')}
                subtitle={t('permit.access.locations', {
                  count: poolingGroups?.length ?? 0,
                })}
                boldSubtitle={true}
                details={t('permit.access.spots_available', {
                  count: availableSpots,
                })}
                testID="access-permit"
                subDetails={
                  usageType === 'CUSTOMER'
                    ? t('permit.customerUsage')
                    : undefined
                }
              />
            }
            belowCutView={
              <PermitContentWrapper>
                {poolingGroups?.map(renderPoolingGroupRow)}
              </PermitContentWrapper>
            }
            backgroundColor={theme.colors.lightBg.lilac}
          />
        </ModalContentWrapper>
      </ScrollView>
    </BottomButtonsLayout>
  );
};

export default MultiPoolPermit;
